import React, { useEffect, useState } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback, Button } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, apiError } from "../../store/actions";
import { getCompanyName } from "constants/constAPI";
import ShreeRamImage from "../../components/Common/ShreeRamImage";

const Register = () => {
  document.title = "Register New Firm | 7-Day Free Trial";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firmName: "",
      ownerName: "",
      mobileNo: "",
      email: "",
      city: "",
      state: "",
      gstNo: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firmName: Yup.string()
        .min(3, "Firm name must be at least 3 characters")
        .required("Please enter Firm Name"),
      ownerName: Yup.string()
        .min(2, "Owner name must be at least 2 characters")
        .required("Please enter Owner / Contact Person name"),
      mobileNo: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Please enter a valid 10-digit Indian mobile number")
        .required("Mobile number is required (used as Login Username)"),
      email: Yup.string()
        .email("Please enter a valid email address"),
      city: Yup.string().required("Please enter City"),
      state: Yup.string().required("Please enter State"),
      password: Yup.string()
        .min(4, "Password must be at least 4 characters")
        .required("Please enter Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Please confirm your Password"),
    }),
    onSubmit: (values) => {
      dispatch(registerUser({
        firmName: values.firmName.trim(),
        ownerName: values.ownerName.trim(),
        mobileNo: values.mobileNo.trim(),
        email: values.email.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        gstNo: values.gstNo.trim(),
        password: values.password,
        username: values.mobileNo.trim(),
      }));
    },
  });

  const AccountProperties = createSelector(
    (state) => state.Account,
    (account) => ({
      user: account.user,
      registrationError: account.registrationError,
      success: account.success,
    })
  );

  const { user, registrationError, success } = useSelector(AccountProperties);

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/login"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  return (
    <React.Fragment>
      <div
        className="account-pages"
        style={{
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          height: "100vh",
          maxHeight: "100vh",
          width: "100vw",
          maxWidth: "100vw",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "10px 15px",
          position: "relative",
        }}
      >
        <Container fluid style={{ maxWidth: "1020px" }}>
          <Row className="justify-content-center">
            <Col xs={12}>
              <Card
                className="overflow-hidden border-0 shadow-lg"
                style={{ borderRadius: "16px", backgroundColor: "#ffffff", maxHeight: "92vh", overflowY: "auto" }}
              >
                {/* Compact Header Bar */}
                <div
                  className="px-4 py-2 text-white d-flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="bg-white rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                      style={{ width: "42px", height: "42px" }}
                    >
                      <ShreeRamImage
                        alt="Logo"
                        portraitStyle={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                        landscapeStyle={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </div>
                    <div>
                      <h5 className="text-white fw-bold mb-0">Register Your Firm</h5>
                      <small className="text-white-50">
                        Multi-Firm Agricultural Brokerage & Ledger Management
                      </small>
                    </div>
                  </div>

                  <span
                    className="badge bg-warning text-dark px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1"
                    style={{ borderRadius: "20px", fontSize: "12px" }}
                  >
                    <i className="fas fa-gift"></i>
                    Instant 7-Day Free Trial Included
                  </span>
                </div>

                <CardBody className="px-4 py-3">
                  {success && (
                    <Alert color="success" className="d-flex align-items-center gap-2 mb-2 py-2">
                      <i className="fas fa-check-circle font-size-18"></i>
                      <div className="font-size-13">
                        <strong>Congratulations!</strong> Your firm has been registered successfully and your 7-day free trial is now active. Redirecting to login...
                      </div>
                    </Alert>
                  )}

                  {registrationError && (
                    <Alert color="danger" className="d-flex align-items-center gap-2 mb-2 py-2">
                      <i className="fas fa-exclamation-triangle font-size-18"></i>
                      <div className="font-size-13">
                        <strong>Registration Error:</strong> {registrationError}
                      </div>
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {/* Row 1: Firm Name, Owner Name, Mobile Number */}
                    <Row className="g-3 mb-2">
                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Firm Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="firmName"
                          placeholder="e.g. Radhey Trading Co."
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firmName}
                          invalid={validation.touched.firmName && !!validation.errors.firmName}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.firmName && validation.errors.firmName && (
                          <FormFeedback className="font-size-11">{validation.errors.firmName}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Owner / Contact Person <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="ownerName"
                          placeholder="e.g. Ramesh Kumar"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.ownerName}
                          invalid={validation.touched.ownerName && !!validation.errors.ownerName}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.ownerName && validation.errors.ownerName && (
                          <FormFeedback className="font-size-11">{validation.errors.ownerName}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Mobile Number (Login ID) <span className="text-danger">*</span>
                        </Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light font-size-12 px-2" style={{ borderRadius: "8px 0 0 8px" }}>+91</span>
                          <Input
                            name="mobileNo"
                            maxLength="10"
                            placeholder="10-digit Mobile No"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.mobileNo}
                            invalid={validation.touched.mobileNo && !!validation.errors.mobileNo}
                            className="form-control font-size-13"
                            style={{ borderRadius: "0 8px 8px 0", padding: "7px 12px" }}
                          />
                        </div>
                        {validation.touched.mobileNo && validation.errors.mobileNo && (
                          <div className="text-danger font-size-11 mt-1">{validation.errors.mobileNo}</div>
                        )}
                      </Col>
                    </Row>

                    {/* Row 2: Email, City, State */}
                    <Row className="g-3 mb-2">
                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Email Address <span className="text-muted font-size-11">(Optional)</span>
                        </Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="firm@example.com"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={validation.touched.email && !!validation.errors.email}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.email && validation.errors.email && (
                          <FormFeedback className="font-size-11">{validation.errors.email}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          City <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="city"
                          placeholder="e.g. Indore"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.city}
                          invalid={validation.touched.city && !!validation.errors.city}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.city && validation.errors.city && (
                          <FormFeedback className="font-size-11">{validation.errors.city}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          State <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="state"
                          placeholder="e.g. Madhya Pradesh"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.state}
                          invalid={validation.touched.state && !!validation.errors.state}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.state && validation.errors.state && (
                          <FormFeedback className="font-size-11">{validation.errors.state}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    {/* Row 3: GSTIN, Password, Confirm Password */}
                    <Row className="g-3 mb-3">
                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          GSTIN <span className="text-muted font-size-11">(Optional)</span>
                        </Label>
                        <Input
                          name="gstNo"
                          placeholder="22AAAAA0000A1Z5"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.gstNo}
                          className="form-control font-size-13 text-uppercase"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Password <span className="text-danger">*</span>
                        </Label>
                        <div className="position-relative">
                          <Input
                            name="password"
                            type={showPass ? "text" : "password"}
                            placeholder="Min 4 characters"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                            className="form-control font-size-13"
                            style={{ borderRadius: "8px", padding: "7px 36px 7px 12px" }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPass(!showPass)}
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted p-1 me-2"
                            style={{ textDecoration: "none" }}
                          >
                            <i className={showPass ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </button>
                        </div>
                        {validation.touched.password && validation.errors.password && (
                          <FormFeedback className="font-size-11">{validation.errors.password}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13">
                          Confirm Password <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="confirmPassword"
                          type={showPass ? "text" : "password"}
                          placeholder="Re-enter password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword}
                          invalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                          className="form-control font-size-13"
                          style={{ borderRadius: "8px", padding: "7px 12px" }}
                        />
                        {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                          <FormFeedback className="font-size-11">{validation.errors.confirmPassword}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    {/* Submit Button */}
                    <div className="d-grid mb-2">
                      <Button
                        type="submit"
                        style={{
                          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          border: "none",
                          borderRadius: "10px",
                          padding: "10px",
                          fontSize: "15px",
                          fontWeight: "600",
                          boxShadow: "0 3px 12px rgba(56, 239, 125, 0.35)",
                        }}
                      >
                        <i className="fas fa-rocket me-2"></i>
                        Activate Firm & Start 7-Day Free Trial
                      </Button>
                    </div>

                    {/* Bottom Links */}
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top font-size-13 text-muted">
                      <div>
                        Already registered?{" "}
                        <Link to="/login" className="text-primary fw-bold">
                          Sign In Here
                        </Link>
                      </div>
                      <div>
                        Need Help? Call:{" "}
                        <a href="tel:+919999999999" className="text-primary fw-bold">
                          +91 99999-99999
                        </a>
                      </div>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Footer */}
        <div style={{ position: "absolute", bottom: "6px", left: 0, right: 0, textAlign: "center" }}>
          <small className="text-white-50">
            © {new Date().getFullYear()} Welcome. All rights reserved.
          </small>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Register;
